import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware'; // ✨ 미들웨어 추가

export interface NotificationMessage {
    id: string;
    title: string;
    content: string;
    timestamp: number;
    read: boolean;
}

interface NotificationState {
    currentNotification: NotificationMessage | null;
    queue: NotificationMessage[];
    history: NotificationMessage[];
    isHistoryOpen: boolean;

    addNotification: (msg: Omit<NotificationMessage, 'id' | 'timestamp' | 'read'>) => void;
    dismissCurrent: () => void;
    clearQueue: () => void;
    toggleHistory: () => void;
    clearHistory: () => void;

    // ✨ 읽지 않은 알림 개수 (Getter 역할)
    getUnreadCount: () => number;
}

const useNotificationStore = create<NotificationState>()(
    persist(
        (set, get) => ({
            currentNotification: null,
            queue: [],
            history: [],
            isHistoryOpen: false,

            addNotification: (msg) => {
                const newNoti: NotificationMessage = {
                    ...msg,
                    id: Math.random().toString(36).substr(2, 9),
                    timestamp: Date.now(),
                    read: false
                };

                set((state) => {
                    if (!state.currentNotification) {
                        return {
                            currentNotification: newNoti,
                            // 히스토리에는 추가하되, 읽지 않은 상태로 저장
                            history: [newNoti, ...state.history]
                        };
                    }
                    return {
                        queue: [...state.queue, newNoti],
                        history: [newNoti, ...state.history]
                    };
                });
            },

            dismissCurrent: () => {
                set((state) => {
                    // 1. 현재 알림을 히스토리에서 '읽음(read: true)'으로 업데이트
                    const currentId = state.currentNotification?.id;
                    const updatedHistory = state.history.map(item =>
                        item.id === currentId ? { ...item, read: true } : item
                    );

                    // 2. 큐에서 다음 알림 꺼내기
                    if (state.queue.length > 0) {
                        const next = state.queue[0];
                        const remainingQueue = state.queue.slice(1);
                        return {
                            currentNotification: next,
                            queue: remainingQueue,
                            history: updatedHistory
                        };
                    }

                    // 3. 큐가 비었으면 종료
                    return {
                        currentNotification: null,
                        queue: [],
                        history: updatedHistory
                    };
                });
            },

            clearQueue: () => {
                set((state) => {
                    // 현재 떠있는 알림 + 큐에 있는 모든 알림의 ID 추출
                    const idsToMarkRead = new Set([
                        state.currentNotification?.id,
                        ...state.queue.map(n => n.id)
                    ].filter(Boolean));

                    // 히스토리에서 해당 ID들을 모두 읽음 처리
                    const updatedHistory = state.history.map(item =>
                        idsToMarkRead.has(item.id) ? { ...item, read: true } : item
                    );

                    return {
                        currentNotification: null,
                        queue: [],
                        history: updatedHistory
                    };
                });
            },

            toggleHistory: () => set((state) => ({ isHistoryOpen: !state.isHistoryOpen })),

            clearHistory: () => set({ history: [] }),

            // ✨ 안 읽은 개수 계산: (현재 알림 + 큐) 개수와 동일하거나, 히스토리의 !read 개수와 동일해야 함
            // 여기서는 history를 기준으로 계산하는 것이 가장 정확함
            getUnreadCount: () => {
                const state = get();
                return state.history.filter(item => !item.read).length;
            }
        }),
        {
            name: 'notification-storage', // localStorage에 저장될 키 이름
            storage: createJSONStorage(() => localStorage), // 저장소 설정 (기본값 localStorage)
            // isHistoryOpen 같은 UI 상태는 굳이 저장 안 해도 되면 partialize 사용 가능 (여기선 다 저장)
        }
    )
);

export default useNotificationStore;